import { useState } from "react";
import { MapPin } from "lucide-react";
import { saveCompanyLocations, type CompanyLocation, type LocationWarning, type PrimeCompany } from "../api/CrmApi";
import LocationsEditor from "./LocationsEditor";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";

interface Props {
  company: PrimeCompany;
  onClose: () => void;
  onSaved?: (company: PrimeCompany) => void;
  /** The offices changed under the form (409): reload the company and close, so reopening starts from the current list. */
  onStale?: () => void;
}

/** The Edit addresses form: only the offices. The server computes each office's coordinates on save and returns
 *  warnings (a zip that belongs to another state, an office it could not place); with warnings the form stays open,
 *  showing the saved result, so a typo can be fixed right away. */
export default function EditAddressesModal({ company, onClose, onSaved, onStale }: Props) {
  const [locations, setLocations] = useState<CompanyLocation[]>(company.locations ?? []);
  // The offices as last seen from the server (on open, then after each save): the save is refused if they changed since.
  const [base, setBase] = useState<CompanyLocation[]>(company.locations ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<LocationWarning[] | null>(null);
  const [stale, setStale] = useState(false);
  const [researchedNone, setResearchedNone] = useState(company.offices_reviewed_at != null && (company.locations ?? []).length === 0);

  async function save() {
    setSaving(true);
    setError(null);
    setStale(false);
    try {
      const result = await saveCompanyLocations(company.id, locations, base, researchedNone && locations.length === 0);
      onSaved?.(result.company);
      if (result.warnings.length === 0) {
        onClose();
        return;
      }
      setLocations(result.company.locations);
      setBase(result.company.locations);
      setWarnings(result.warnings);
    } catch (err: any) {
      setStale(err?.response?.status === 409);
      setError(err?.response?.data?.detail || err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title="Edit addresses"
      description={company.name || company.domain || "company"}
      icon={<MapPin />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            {warnings ? "Done" : "Cancel"}
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? "Saving…" : warnings ? "Save again" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-[11px] text-muted-foreground">
          Every known office. Each save places the office on the map from its city, state and zip; a radius rule
          (&ldquo;within 50 miles of&hellip;&rdquo;) measures from the nearest office that counts. A mailing address or virtual
          office does not count as local presence &mdash; mark it so.
        </p>
        <LocationsEditor value={locations} onChange={setLocations} disabled={saving} />
        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={researchedNone && locations.length === 0}
            disabled={saving || locations.length > 0}
            onChange={(e) => setResearchedNone(e.target.checked)}
            className="accent-primary"
          />
          Researched — no address found
        </label>
      </div>
      {warnings && (
        <Alert tone="warning" className="mt-4">
          <p className="font-medium">Saved, with {warnings.length === 1 ? "one thing" : `${warnings.length} things`} to check:</p>
          <ul className="list-disc pl-5 mt-1 space-y-0.5">
            {warnings.map((w, i) => (
              <li key={i}>Office {w.office_index + 1}: {w.message}</li>
            ))}
          </ul>
        </Alert>
      )}
      {error && (
        <Alert tone="danger" className="mt-4">
          {error}
          {stale && onStale && (
            <button type="button" onClick={onStale} className="ml-2 font-medium underline">
              Reload latest
            </button>
          )}
        </Alert>
      )}
    </Modal>
  );
}
